'use client'
import { useTranslation } from 'react-i18next'
import { FaCheck, FaGem } from 'react-icons/fa'
import Modal from './Modal'
import {
  PLAN_LABEL,
  PLAN_PERK_KEYS,
  UPGRADE_FEATURES,
} from '../config/upgradeFeatures'

// The per-feature replacement for the generic limit modal.
//
// Wraps the shared <Modal> rather than re-implementing a dialog, so it inherits the portal, the
// z-[110] tier, the keyboard inset and the overflow handling every other modal already gets
// right. Only the CONTENT is bespoke.
//
// Driven entirely by an id from UPGRADE_FEATURES — call sites never pass copy, so a feature
// reads identically wherever it is gated (the create panel, the video panel, the pose picker,
// chat).
const FeatureUpgradeModal = ({
  featureId,
  overrides, // only for data-driven tiers (see openUpgrade)
  isOpen,
  closeModal,
  onUpgrade,
  secondary, // optional { label, onClick } — a way out that isn't "pay us"
}) => {
  const { t } = useTranslation()

  const base = UPGRADE_FEATURES[featureId]
  const feature = base && overrides ? { ...base, ...overrides } : base
  // Unknown id: render nothing rather than an empty dialog. A gate naming a feature we have no
  // copy for is a bug at the call site, and a blank modal would hide it.
  if (!feature) return null

  const planLabel = PLAN_LABEL[feature.plan] ?? PLAN_LABEL.pro
  const perkKeys = PLAN_PERK_KEYS[feature.plan] ?? []
  // `name` may be overridden with an already-resolved string (a pose title, a voice name), so
  // prefer it over the key.
  const name = feature.name ?? t(feature.nameKey)

  return (
    <Modal
      isOpen={isOpen}
      closeModal={closeModal}
      title={
        <span className="flex items-center gap-2 pr-8">
          <span aria-hidden="true">{feature.emoji}</span>
          <span>{name}</span>
          <span className="bg-[var(--main-bg-color)] text-white text-[10px] font-bold uppercase tracking-wide rounded-full px-2 py-0.5">
            {planLabel}
          </span>
        </span>
      }
      content={
        <div className="text-left">
          <p className="text-base text-white leading-relaxed">
            {t(feature.taglineKey)}
          </p>

          <ul className="mt-4 space-y-2">
            {feature.benefitKeys.map((benefitKey) => (
              <li
                key={benefitKey}
                className="flex items-start gap-2.5 text-sm text-gray-200"
              >
                <span className="mt-0.5 shrink-0 w-4 h-4 rounded-full bg-[var(--main-bg-color)] flex items-center justify-center">
                  <FaCheck size={8} className="text-white" />
                </span>
                <span>{t(benefitKey)}</span>
              </li>
            ))}
          </ul>

          {feature.noteKey && (
            <p className="mt-4 text-xs text-gray-400 italic leading-relaxed">
              {t(feature.noteKey)}
            </p>
          )}

          {perkKeys.length > 0 && (
            <>
              <hr className="my-4 border-[var(--foreground-bg-color)]" />
              <p className="text-xs font-semibold text-gray-300 flex items-center gap-1.5">
                <FaGem size={9} />
                {t('upgradeGateAlsoIncluded', { plan: planLabel })}
              </p>
              <ul className="mt-2 space-y-1">
                {perkKeys.map((perkKey) => (
                  <li key={perkKey} className="text-xs text-gray-400">
                    • {t(perkKey)}
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      }
      confirmation={t('upgradeGateCta', { plan: planLabel })}
      onConfirm={onUpgrade}
      confirmation2={secondary?.label}
      onConfirm2={secondary?.onClick}
    />
  )
}

export default FeatureUpgradeModal
